import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Save, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGES = ['home', 'about', 'services', 'government', 'contact', 'careers'];

export default function SeoEditor() {
  const [activePage, setActivePage] = useState('home');
  const [seo, setSeo] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchSeo = useCallback(async (page) => {
    try {
      const res = await axios.get(`${API}/api/admin/seo/${page}`, { withCredentials: true });
      setSeo(res.data || {});
    } catch { setSeo({}); }
  }, []);

  useEffect(() => { fetchSeo(activePage); setSaved(false); }, [activePage, fetchSeo]);

  const up = (k, v) => { setSeo(prev => ({ ...prev, [k]: v })); setSaved(false); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/seo/${activePage}`, seo, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">SEO Metadata</h1>
        <button onClick={handleSave} disabled={saving} className="bg-[#F2B705] text-black font-bold text-xs uppercase px-5 py-2.5 hover:bg-[#D9A404] flex items-center gap-2 disabled:opacity-50">
          {saved ? <><CheckCircle size={14} /> Saved</> : <><Save size={14} /> {saving ? 'Saving...' : 'Save'}</>}
        </button>
      </div>

      <div className="flex gap-1 mb-8 overflow-x-auto pb-2">
        {PAGES.map(p => (
          <button key={p} onClick={() => setActivePage(p)}
            className={`px-4 py-2 text-xs uppercase tracking-wide font-bold transition-colors capitalize whitespace-nowrap ${
              activePage === p ? 'bg-[#F2B705] text-black' : 'bg-[#161616] text-[#888] hover:text-white border border-white/10'
            }`}>
            {p}
          </button>
        ))}
      </div>

      <div className="bg-[#161616] border border-white/10 p-6 space-y-5">
        <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Page Title</label>
          <Input value={seo.title || ''} onChange={e => up('title', e.target.value)} className="bg-transparent border-white/10 text-white text-sm h-10 focus:border-[#F2B705]" /></div>
        <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Meta Description</label>
          <Textarea value={seo.description || ''} onChange={e => up('description', e.target.value)} rows={3} className="bg-transparent border-white/10 text-white text-sm focus:border-[#F2B705] resize-none" /></div>
        <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Keywords</label>
          <Input value={seo.keywords || ''} onChange={e => up('keywords', e.target.value)} className="bg-transparent border-white/10 text-white text-sm h-10 focus:border-[#F2B705]" /></div>
        <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">OG Title</label>
          <Input value={seo.og_title || ''} onChange={e => up('og_title', e.target.value)} className="bg-transparent border-white/10 text-white text-sm h-10 focus:border-[#F2B705]" /></div>
        <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">OG Description</label>
          <Textarea value={seo.og_description || ''} onChange={e => up('og_description', e.target.value)} rows={2} className="bg-transparent border-white/10 text-white text-sm focus:border-[#F2B705] resize-none" /></div>
      </div>
    </div>
  );
}
